import { SwipeableDrawer, Card, CardContent, Stack, Button, useMediaQuery, Box, List, MenuItem, Typography, 
  IconButton, ListItem, ListItemText, ListSubheader, Divider, styled, Tab, TypographyProps, Tooltip } from "@mui/material";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FieldChanged, FieldWithEncryptorInfo, HintEntry, UpgradeStatus } from "~/gen/api/encrypt";
import { TableField, useESCRenameHintContext } from "./ESCRenameHintContext";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import chainOptions from "~/utils/chainOptions";
import { useEditContext } from "ra-core";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useDbEscStatusContext } from "~/pages/databases/DatabaseEncryptionSettingChange";


const HintFromToComposer : FC<{from?:TableField|null, to?:TableField|null, setStates:Dispatch<SetStateAction<State>>}> = ({
  from,
  to,
  setStates,
}) => {
  const { addHint } = useESCRenameHintContext()
  const { data: dbStatus } = useDbEscStatusContext()
  const renameOnTable = ![from, to].some(x => Boolean(x?.fieldName))
  const clear = () => setStates(v => ({...v, 
    ...(renameOnTable
      ? {fromTable:null, toTable:null}
      : {fromField:null, toField:null}
    )
  }))

  return (
    <HintFromToComposerWrapper>
      <Stack direction="row" alignItems="center" className="text-fields">
        <Box>
          {from
            ? renameOnTable 
              ? <DisplayTableName data={from} variant="subtitle2" color="error.main" />
              : <DisplayFieldName data={from} variant="subtitle2" color="error.main" className="singleline" />

            : <Typography variant="body1" color="InactiveCaptionText">Before</Typography>
          }
        </Box>
        <ArrowForwardIcon />
        <Box>
          {to
            ? renameOnTable 
              ? <DisplayTableName data={to} variant="subtitle2" color="success.main" />
              : <DisplayFieldName data={to} variant="subtitle2" color="success.main" className="singleline" />

            : <Typography variant="body1" color="InactiveCaptionText">After</Typography>
          }
        </Box>
      </Stack>
      <Stack direction="row" sx={{ my: 1 }}>
        {!from && !to
          ? <Typography variant="body2" sx={{ mx: 'auto', lineHeight: '40px' }}>Choose from the below lists:</Typography>
          : <>
              <Tooltip title="Confirm Add Hint">
                <IconButton 
                  sx={{ ml: 'auto' }}
                  disabled={dbStatus?.upgradeStatus !== UpgradeStatus.Unlocked || !from || !to} color="primary"
                  onClick={() => {
                    if (!from || !to) { return }
                    addHint(from, to)
                    clear()
                  }}
                >
                  <DoneIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear Selection">
                <IconButton 
                  sx={{ mr: 'auto' }}
                  disabled={!from && !to} color="error" 
                  onClick={clear}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </>
        }
      </Stack>

    </HintFromToComposerWrapper>
  )
}

const HintFromToComposerWrapper = styled(Box)(({theme}) => ({
  '& div.text-fields div.MuiBox-root': { 
    height: 36, 
    flex: '0 1 50%', 
    border: `solid 1px ${theme.palette.action.disabled}`, 
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderRadius: theme.spacing(0.5), 
    backgroundColor: theme.palette.background.paper,
    '& h6.MuiTypography-root': { lineHeight: '14px' },
    '& h6.MuiTypography-root.singleline': { lineHeight: '36px' },
    '& p.MuiTypography-body1': { lineHeight: '36px' },
  }
}))

const HintFromToPicker : FC<{ addArr:FieldWithEncryptorInfo[], removeArr:FieldWithEncryptorInfo[], 
  setStates:Dispatch<SetStateAction<State>>, renameOnTable:boolean
}> = ({
  addArr,
  removeArr,
  setStates,
  renameOnTable,
}) => {
  return (
    <HintFromToPickerWrapper direction="row" justifyContent="space-between" spacing={2}>
      <List>
        {removeArr.map(f => {
          const k = renameOnTable ? `${f.schemaName}-${f.tableName}` : f.fieldName
          const onClick = () => setStates(v => ({ ...v,
            ...(renameOnTable 
              ? {fromTable: { schemaName: f.schemaName, tableName: f.tableName }}
              : {fromField: f})
          }))
          return (
            <MenuItem key={k} onClick={onClick}>
              {renameOnTable
                ?<DisplayTableName data={f} variant="body2" color="error.light" />
                :<DisplayFieldName data={f} variant="body2" color="error.light" />
              }
            </MenuItem>
          )
        })}
      </List>
      <List>
        {addArr.map(f => {
          const k = renameOnTable ? `${f.schemaName}-${f.tableName}` : f.fieldName
          const onClick = () => setStates(v => ({ ...v,
            ...(renameOnTable 
              ? {toTable: { schemaName: f.schemaName, tableName: f.tableName }}
              : {toField: f})
          }))
          return (
            <MenuItem key={k} onClick={onClick}>
              {renameOnTable
                ?<DisplayTableName data={f} variant="body2" color="success.light" />
                :<DisplayFieldName data={f} variant="body2" color="success.light" />
              }
            </MenuItem>
          )
        })}
      </List>
    </HintFromToPickerWrapper>
  )
}

const HintFromToPickerWrapper = styled(Stack)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
  margin: theme.spacing(1),
  '& ul.MuiList-root': {
    flex: '0 1 50%',
    backgroundColor: theme.palette.background.paper,
    maxHeight: `calc(100vh - 641px)`,
    overflowY: 'auto',
  }
}))

const DisplayTableName : FC<{variant:string, color:string, data:TableField}> = (props) => {
  const {data, ...rest} = props
  return (
    <Typography {...(rest as any)}>
      <Typography variant="caption" color="InactiveCaptionText">
        {data.schemaName}.
      </Typography><br />
      {data.tableName}
    </Typography>
  )
}

const DisplayFieldName : FC<{data:TableField}&TypographyProps> = (props) => {
  const {data, ...rest} = props
  return (
    <Typography {...(rest as any)}>
      {data.fieldName}
    </Typography>
  )
}

interface TableChangeData {
  addedTableFields: Record<string, FieldWithEncryptorInfo[]>;
  removedTableFields: Record<string, FieldWithEncryptorInfo[]>;
  changedTableFields: Record<string, FieldChanged[]>;
}

type EffectiveHints = Record<string, {
  renamedOnTable: boolean;
  hints: HintEntry[];
}>

export interface ESCRenameHintEditorProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

interface State {
  tab: 'table'|'column',
  fromTable: TableField|null,
  toTable: TableField|null,
  fromField: TableField|null,
  toField: TableField|null,
}

const ESCRenameHintEditor: FC<ESCRenameHintEditorProps> = ({
  open,
  setOpen,
}) => {
  const { refetch: refetchESCData } = useEditContext()
  const { data: dbStatus } = useDbEscStatusContext()
  const { saveHints, resetEdits, hasChanges, saving, refetchHints } = useESCRenameHintContext()
  const handleClose = (): void => {
    setOpen(false);
  };
  
  const match = useMediaQuery('(max-width: 680px)')
  const width = match ? 'calc(100vw - 50px)' : 630

  var possibleRenameIncidents = useContext(PossibleRenameIncidentsContext)

  const [states, setStates] = useState<State>({
    tab: 'table',
    fromTable: null,
    toTable: null,
    fromField: null,
    toField: null,
  })
  
  const handlers = useDefaultSavedHandlers("Save Rename Hints")
  const performSave = () => {
    if (dbStatus?.upgradeStatus !== UpgradeStatus.Unlocked || !hasChanges || saving) { return }
    saveHints(chainOptions(handlers, {
      onSuccess: () => {
        refetchESCData && refetchESCData()
        refetchHints()
        resetEdits()
        setOpen(false)
      }
    }))
  }

  const handleTabChange = (event: any, newVal: 'table'|'column') => {
    setStates(v => ({...v, tab: newVal }))
  }

  return (
    <SwipeableDrawer
      anchor="right"
      onOpen={() => {}}
      onClose={handleClose}
      open={open}
      PaperProps={{ sx: { p: 2, width: width, }}}
    >
      <Card sx={{ overflowY: 'auto', mb: 4, }}>
        <CardContent sx={{ bgcolor: 'background.default', pt: 0, '& > div.MuiTabPanel-root': { p: 0 } }}>
          <TabContext value={states.tab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleTabChange} aria-label="Choose to edit table or column renames">
                <Tab label="Hint Table" value="table" />
                <Tab label="Hint Column" value="column" />
              </TabList>
            </Box>
            <TabPanel value="table">
              <TableRenameEditor states={states} setStates={setStates} possibleRenameIncidents={possibleRenameIncidents} />
            </TabPanel>
            <TabPanel value="column">
              <ColumnRenameEditor states={states} setStates={setStates} possibleRenameIncidents={possibleRenameIncidents} />
            </TabPanel>
          </TabContext>
          <Stack direction="row" sx={{ justifyContent: 'space-between', py: 2, mt: 2, mx: -2, px: 2, mb: -3, bgcolor: 'background.paper' }} >
            <Button
              color="primary"
              disabled={saving}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={!hasChanges || saving}
              type="submit"
              variant="contained"
              onClick={performSave}
            >
              Save
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </SwipeableDrawer>
  )
}

export default ESCRenameHintEditor;


const TableRenameEditor : FC<{states:State, setStates:Dispatch<SetStateAction<State>>, possibleRenameIncidents:PossibleRenameIncidents}> = ({
  states,
  setStates,
  possibleRenameIncidents,
}) => {
  const { removeHint, effectiveHints } = useESCRenameHintContext()
  const { data: dbStatus } = useDbEscStatusContext()
  const renamedTables = getHintedTableRenames(effectiveHints)
  return (
    <>
      <Box sx={{ bgcolor: 'background.default', py: 1, px: 2, mb: 2, mx: -2 }}>
        <List sx={{ bgcolor: 'background.paper' }}
          aria-labelledby="table-rename-subheader"
          subheader={
            <ListSubheader id="table-rename-subheader">
              Table Renames
            </ListSubheader>
          }
        >
          <Divider />
          {renamedTables.length === 0 &&
            <ListItem divider={true}>
              <ListItemText>
                <Typography variant="caption" color="InactiveCaptionText" textAlign="center">No Data</Typography>
              </ListItemText>
            </ListItem>
          }
          {renamedTables.map(h => (
            <ListItem key={`${h?.schemaName}-${h?.oldTableName}`} 
              divider={true}
              secondaryAction={
                <Tooltip title="Remove Hint">
                  <IconButton color="warning" size="small"
                    disabled={dbStatus?.upgradeStatus !== UpgradeStatus.Unlocked}
                    onClick={() => removeHint(h)} 
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText>
                <Typography variant="body2" component="span" color="error.light" sx={{ textDecoration: 'line-through' }}>
                  <Typography variant="caption" color="InactiveCaptionText">
                    {h?.schemaName}.{" "}
                  </Typography>
                  {h?.oldTableName}
                </Typography>
                <Typography variant="body2" sx={{ ml: 2 }} component="span" color="success.light">
                  <Typography variant="caption" color="InactiveCaptionText">
                    {h?.newSchemaName || h?.schemaName}.{" "}
                  </Typography>
                  {h?.newTableName}
                </Typography>
              </ListItemText>
            </ListItem>
          ))}
        </List>
      </Box>
      <HintFromToComposer from={states.fromTable} to={states.toTable} setStates={setStates} />
      <Box>
        <HintFromToPicker renameOnTable={true} setStates={setStates} 
          removeArr={Object.keys(possibleRenameIncidents.removedTablesBag).sort().map(k => possibleRenameIncidents.removedTablesBag[k][0])} 
          addArr={Object.keys(possibleRenameIncidents.addedTablesBag).sort().map(k => possibleRenameIncidents.addedTablesBag[k][0])} 
        />
      </Box>
    </>
  )
}

const ColumnRenameEditor : FC<{states:State, setStates:Dispatch<SetStateAction<State>>, possibleRenameIncidents:PossibleRenameIncidents}> = ({
  states,
  setStates,
  possibleRenameIncidents,
}) => {
  const { removeHint, effectiveHints } = useESCRenameHintContext()
  const { data: dbStatus } = useDbEscStatusContext()
  
  const groupedFieldHints = Object.keys(effectiveHints).flatMap(k => {
    const group = effectiveHints[k]
    const fieldHints = group.hints.filter(h => !!h.oldFieldName)
    if (fieldHints.length === 0) {
      return []
    }
    const fst = fieldHints[0]
    return {
      key: `${fst.newSchemaName || fst.schemaName}-${fst.newTableName}`,
      fst,
      hints: fieldHints.sort((a, b) => (a.newFieldName || '') < (b.newFieldName || '') ? -1 : 1)
    }
  }).sort((a, b) => a.key < b.key ? -1 : 1)

  const ctrls = useInnerCarouselContextController(possibleRenameIncidents, setStates)
  const { tableBag } = ctrls

  return (
    <>
      <Box sx={{ bgcolor: 'background.default', py: 1, px: 2, mb: 2, mx: -2 }}>
        <List 
          sx={{ 
            bgcolor: 'background.paper',
            '& ul.table-group': {
              pl: 3,
              '& > li.MuiListSubheader-root': {
                lineHeight: '32px',
                mb: -1.5,
                pl: 0,
              },
            },
          }}
          aria-labelledby="column-rename-subheader"
          subheader={
            <ListSubheader id="column-rename-subheader">
              Column Renames
            </ListSubheader>
          }
        >
          <Divider />
          <Box sx={{ maxHeight: 226, overflow: 'auto', }}>
            {groupedFieldHints.length === 0 &&
              <ListItem divider={true}>
                <ListItemText>
                  <Typography variant="caption" color="InactiveCaptionText" textAlign="center">No Data</Typography>
                </ListItemText>
              </ListItem>
            }
            {groupedFieldHints.map(g => {
              const fst = g.fst
              return (
                <li key={g.key}>
                  <ul className="table-group">
                    <ListSubheader>
                      <Typography variant="caption" color="InactiveCaptionText">
                        {fst.newSchemaName || fst.schemaName}. {fst.newTableName}.
                      </Typography>
                    </ListSubheader>
                    {g.hints.map(h => (
                      <ListItem key={`${h.newFieldName}`} 
                        divider={true}
                        secondaryAction={
                          <Tooltip title="Remove Hint">
                            <IconButton color="warning" size="small"
                              disabled={dbStatus?.upgradeStatus !== UpgradeStatus.Unlocked}
                              onClick={() => removeHint(h)} 
                            >
                              <RemoveCircleOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemText>
                          <Typography variant="body2" component="span" color="error.light" sx={{ textDecoration: 'line-through' }}>
                            {h?.oldFieldName}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2 }} component="span" color="success.light">
                            {h?.newFieldName}
                          </Typography>
                        </ListItemText>
                      </ListItem>
                    ))}
                  </ul>
                </li>
              )
            })}
          </Box>
        </List>
      </Box>
      <HintFromToComposer from={states.fromField} to={states.toField} setStates={setStates} />
      {tableBag &&
        <Box>
          <InnerCarouselContext.Provider value={ctrls}>
            <TableContextSelectCarousel />
          </InnerCarouselContext.Provider>
          <HintFromToPicker renameOnTable={false} setStates={setStates} 
            removeArr={tableBag.removedFields.sort((a, b) => (a.fieldName || '') < (b.fieldName || '') ? -1 : 1)} 
            addArr={tableBag.addedFields.sort((a, b) => (a.fieldName || '') < (b.fieldName || '') ? -1 : 1)} 
          />
        </Box>
      }
    </>
  )
}

interface InnerCarouselContextProps { 
  tableBag: { addedFields: FieldWithEncryptorInfo[]; removedFields: FieldWithEncryptorInfo[]; }|null, 
  tableContexts: { key: string; fst: FieldWithEncryptorInfo; }[], 
  ctxIndex: number,
  syncCtxIndex: (x:number)=>void,
}
const InnerCarouselContext = createContext<InnerCarouselContextProps>({ tableBag:null, tableContexts:[], ctxIndex:0, syncCtxIndex:()=>{} })
const useInnerCarouselContextController = (
  possibleRenameIncidents: PossibleRenameIncidents,
  setStates:Dispatch<SetStateAction<State>>,
):InnerCarouselContextProps => {
  const tableContexts = useMemo(() => Object.keys(possibleRenameIncidents.tablesBothAddRemovedBag).sort().map(k => {
    return {
      key: k,
      fst: possibleRenameIncidents.tablesBothAddRemovedBag[k].addedFields[0]
    }
  }), [possibleRenameIncidents])
  const [ctxIndex, setCtxIndex] = useState(0)
  const syncCtxIndex = useCallback((k: number) => {
    setCtxIndex(k0 => {
      if (k0 !== k) {
        setTimeout(() => setStates(v => ({...v, fromField:null, toField:null })))
      }
      return k
    })
  }, [setCtxIndex, setStates])
  const tableBag = tableContexts[ctxIndex] 
    ? possibleRenameIncidents.tablesBothAddRemovedBag[tableContexts[ctxIndex].key]
    : null
  useEffect(() => {
    if (!tableBag && tableContexts.length > 0) {
      const [sign, absDelta] = [ctxIndex > 0 ? 1 : -1, Math.abs(ctxIndex - 0)];
      const result = [...new Array(absDelta).keys()].map(d => ctxIndex - sign * (d + 1)).reduce((acc, i) => {
        if (acc.ctx) { return acc }
        return { ctx: tableContexts[i], i }
      }, { ctx: null as any, i: ctxIndex })
      syncCtxIndex(result.i)
    }
  }, [tableBag, tableContexts, ctxIndex, syncCtxIndex])
  return { tableBag, tableContexts, ctxIndex, syncCtxIndex }
}

const TableContextSelectCarousel : FC = () => {
  const { tableContexts, ctxIndex, syncCtxIndex } = useContext(InnerCarouselContext)
  const extList = [
    {...tableContexts[tableContexts.length - 1], key: 'ext-head' }, 
    ...tableContexts, 
    {...tableContexts[0], key: 'ext-tail' },
    {...tableContexts[0], key: 'ext-tail1' },
  ]
  const calcOffset = (index:number) => {
    const [w, initOffset] = [600, -450] // [1, -0.4]
    return initOffset - (index * (500))
  }
  const [states, setStates] = useState({
    offset: calcOffset(ctxIndex),
    target: calcOffset(ctxIndex),
  })
  useEffect(() => {
    const offset = calcOffset(ctxIndex)
    setStates(v => {
      if (v.offset !== offset) {
        return { offset, target: offset }
      } else {
        return v
      }
    })
  }, [ctxIndex])

  const slideTo = (index:number) => {
    if (-1 <= index && index <= tableContexts.length) {
      const [target, anchor] = [calcOffset(index), states.offset]
      const millis = 150
      const unit = (target - states.offset) / millis
      setStates(v => ({...v, target }))
      let start = null as null|number

      const move = (timestamp:number) => {
        if (!start) { start = timestamp }
        const progress = timestamp - start

        setStates(v => ({...v, offset: anchor + progress * unit }))

        if (progress < millis) {
          requestAnimationFrame(move)
        } else {
          const finalIndex = 
              index === -1 ? tableContexts.length - 1
            : index === tableContexts.length ? 0
            : index
          const finalTarget = calcOffset(finalIndex)
          syncCtxIndex(finalIndex)
          setStates(v => ({...v, offset: finalTarget, target: finalTarget }))
        }
      }
      requestAnimationFrame(move)
    }
  }
  return (
    <Box 
      sx={{
        height: 32,
        width: 600,
        mx: '-1px',
        mb: -0.75,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        position: 'relative',
        '& div.carousel-row': {
          whiteSpace: 'nowrap',
          ml: `${states.offset}px`,
          '& > div': {
            display: 'inline-block',
            textAlign: 'center',
            width: 500,
            borderLeft: theme => `solid ${theme.palette.background.default} 1px`,
            borderRight: theme => `solid ${theme.palette.background.default} 1px`,
            '& h6': {
              lineHeight: '32px',
            },
          },
        },
        '& button.MuiIconButton-root': {
          position: 'absolute',
          top: 0,
        },
      }}
    >
      <Box className="carousel-row">
        {extList.map(ctx => (
          <div key={ctx.key}>
            <Typography variant="subtitle1" color="info.main">
              <Typography variant="caption" color="InactiveCaptionText">
                {ctx.fst.schemaName}. 
              </Typography>{" "}
              {ctx.fst.tableName}
            </Typography>
          </div>
        ))}
      </Box>
      <Tooltip title="Previous">
        <IconButton size="small" sx={{ left: 0 }} color="primary" 
          disabled={states.offset !== states.target}
          onClick={() => slideTo(ctxIndex - 1)}
        >
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Next">
        <IconButton size="small" sx={{ right: 0 }} color="primary"
          disabled={states.offset !== states.target}
          onClick={() => slideTo(ctxIndex + 1)}
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
}


const getHintedTableRenames = (
  effectiveHints: Record<string, {
    renamedOnTable: boolean;
    hints: HintEntry[];
  }>,
): HintEntry[] => {
  return Object.values(effectiveHints).filter(group => group.renamedOnTable)
    .map(group => group.hints.find(h => !h.oldFieldName))
    .filter(h => !!h) as any
}

const getHintedFields = (effectiveHints:EffectiveHints) => {
  return Object.values(effectiveHints).flatMap(group => group.hints.filter(h => !!h.oldFieldName))
}


const stripOutFieldsWithSameName = (aArr: FieldWithEncryptorInfo[], rArr: FieldWithEncryptorInfo[]) => {
  const aBag = (aArr || []).reduce((acc1, f) => {
    acc1[f.fieldName || ''] = f
    return acc1
  }, {} as Record<string, FieldWithEncryptorInfo>)
  const removedFields = (rArr || []).reduce((acc2, f) => {
    const fname = f.fieldName || ''
    if (aBag[fname]) {
      delete aBag[fname]
    } else {
      acc2.push(f)
    }
    return acc2
  }, [] as FieldWithEncryptorInfo[])
  return {
    addedFields: Object.values(aBag),
    removedFields,
  }
}

interface PossibleRenameIncidents {
  addedTablesBag: Record<string, FieldWithEncryptorInfo[]>;
  removedTablesBag: Record<string, FieldWithEncryptorInfo[]>;
  tablesBothAddRemovedBag: Record<string, {
    addedFields: FieldWithEncryptorInfo[];
    removedFields: FieldWithEncryptorInfo[];
  }>;
  hasPossibleIncidents: boolean;
}

const PossibleRenameIncidentsContext = createContext({} as PossibleRenameIncidents)

export const PossibleRenameIncidentsContextProvider : FC<{value: PossibleRenameIncidents}> = ({
  value,
  children,
}) => {
  return (
    <PossibleRenameIncidentsContext.Provider value={value}>
      {children}
    </PossibleRenameIncidentsContext.Provider>
  )
}

export const usePossibleRenameIncidents = (
  tableChangeData: TableChangeData, 
  effectiveHints: EffectiveHints
): PossibleRenameIncidents => {
  return useMemo(() => {
    const addedTables = 
      Object.keys(tableChangeData.addedTableFields).filter(keySchemaTable => {
        return !tableChangeData.addedTableFields[keySchemaTable][0].isView
      })
    const removedTables = 
      Object.keys(tableChangeData.removedTableFields).filter(keySchemaTable => {
        return !tableChangeData.removedTableFields[keySchemaTable][0].isView
      })
    const removedSet = new Set(removedTables)
      
    const tablesBothAddRemoved = []

    // compare addedTables and removedTables, to get tablesBothAddRemoved; 
    // this is, at this step, only based on schema change info
    for(let keySchemaTable of removedTables) {
      const i = addedTables.indexOf(keySchemaTable)
      if (i > -1) {
        tablesBothAddRemoved.push(keySchemaTable)
        addedTables.splice(i, 1)
        removedSet.delete(keySchemaTable)
      }
    }

    const addedTablesBag = addedTables.reduce((acc, keySchemaTable) => {
      acc[keySchemaTable] = [...tableChangeData.addedTableFields[keySchemaTable]]
      return acc
    }, {} as Record<string, FieldWithEncryptorInfo[]>)
    const removedTablesBag = [...removedSet].reduce((acc, keySchemaTable) => {
      acc[keySchemaTable] = [...tableChangeData.removedTableFields[keySchemaTable]]
      return acc
    }, {} as Record<string, FieldWithEncryptorInfo[]>)
    const tablesBothAddRemovedBag =
      tablesBothAddRemoved.reduce((acc, keySchemaTable) => {
        acc[keySchemaTable] = stripOutFieldsWithSameName(
          tableChangeData.addedTableFields[keySchemaTable],
          tableChangeData.removedTableFields[keySchemaTable]
        )

        return acc
      }, {} as Record<string, { addedFields: FieldWithEncryptorInfo[], removedFields: FieldWithEncryptorInfo[], }>)
    
    // now let's consider the renameHints, and adjust tablesBothAddRemoved.
    // firstly, with rename on tables
    const renamedTables = getHintedTableRenames(effectiveHints)
    for(let hint of renamedTables) {
      const keySchemaTable_1 = `${hint.schemaName}-${hint.oldTableName}`
      const keySchemaTable_2 = `${hint.newSchemaName || hint.schemaName}-${hint.newTableName}`
      
      const tableWithAddRemove = stripOutFieldsWithSameName(
        addedTablesBag[keySchemaTable_2],
        removedTablesBag[keySchemaTable_1]
      )
      if (tableWithAddRemove.addedFields.length > 0 && tableWithAddRemove.removedFields.length > 0) {
        tablesBothAddRemovedBag[keySchemaTable_1] = tableWithAddRemove
      }
      
      delete removedTablesBag[keySchemaTable_1]
      delete addedTablesBag[keySchemaTable_2]
    }

    // then, with rename on fields
    Object.values(effectiveHints).flatMap(group => {
      const hints = group.hints.filter(h => !!h.oldFieldName)
      if (hints.length === 0) { return [] }
      const fst = hints[0]
      const keySchemaTable = `${fst.schemaName}-${fst.oldTableName}`
      return [{ keySchemaTable, hints }]
    
    }).forEach(({ keySchemaTable, hints }) => {
      const data = tablesBothAddRemovedBag[keySchemaTable]
      if (!data) { return }
      const { addedFields, removedFields } = data
      for(let hint of hints) {
        const i = addedFields.findIndex(f => f.fieldName === hint.newFieldName)
        const j = removedFields.findIndex(f => f.fieldName === hint.oldFieldName)
        if (i > -1) { addedFields.splice(i, 1) }
        if (j > -1) { removedFields.splice(j, 1) }
      }
      if (addedFields.length === 0 || removedFields.length === 0) {
        delete tablesBothAddRemovedBag[keySchemaTable]
      }
    })

    return {
      addedTablesBag,
      removedTablesBag,
      tablesBothAddRemovedBag,
      hasPossibleIncidents: 
        Object.keys(tablesBothAddRemovedBag).length > 0
        || (Object.keys(addedTablesBag).length > 0 && Object.keys(removedTablesBag).length > 0 )
    }
  }, [tableChangeData, effectiveHints])
}
